import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, KeyRound, Minus, ShieldCheck } from "lucide-react"
import { useId, useState } from "react"
import { Link } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { Insignia } from "@/componentes/ui/Insignia"
import { LARGO_MINIMO_CLAVE } from "@/lib/contrasenas"

/**
 * La ficha de un usuario: lo que es, y lo que se le puede cambiar.
 *
 * ⚠ **Se edita el nombre, los roles y si está activo. El correo NO.** Es la
 * identidad con la que entra y con la que quedó firmada cada línea de la bitácora:
 * cambiarlo convertiría el rastro de una persona en el de otra. Si alguien cambia de
 * correo, se crea una cuenta y se desactiva la anterior — que es también lo que
 * conserva quién hizo qué.
 *
 * ⚠ **El tipo tampoco.** `INTERNO` y `CLIENTE` deciden si esa cuenta ve el portal o
 * la bandeja del personal, y un `CLIENTE` sin `cliente_id` no puede existir: es la
 * frontera de aislamiento. Cambiarlo en caliente dejaría a alguien mirando pantallas
 * que no le tocan mientras el embudo de alcance no sabe por dónde recortar.
 *
 * ⚠ **El visto de Telegram se lee, no se pulsa.** Enlazar exige un código que sólo
 * puede pedir la propia persona desde su bandeja de avisos, y escribírselo al bot.
 * Un administrador no puede enlazar a otro: haría falta el chat de esa persona, y
 * eso es justo lo que el código de un solo uso impide falsificar.
 */

export type UsuarioFila = {
  id: number
  email: string
  nombre: string
  tipo: "INTERNO" | "CLIENTE"
  cliente_id: number | null
  activo: boolean
  roles: string[]
  telegram_vinculado: boolean
  es_dev: boolean
}

export type RolFila = { id: number; nombre: string; descripcion: string }

type Props = {
  usuario: UsuarioFila
  roles: RolFila[]
  onGuardado: () => void
}

export function FichaUsuario({ usuario, roles, onGuardado }: Props) {
  const idBase = useId()
  const clienteQuery = useQueryClient()
  const [nombre, setNombre] = useState(usuario.nombre)
  const [elegidos, setElegidos] = useState<string[]>(usuario.roles)
  const [clave, setClave] = useState("")
  const [hecho, setHecho] = useState(false)

  const guardar = useMutation({
    mutationFn: (cambios: Record<string, unknown>) =>
      api.patch(`/api/v1/usuarios/${usuario.id}`, cambios),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["usuarios"] })
      onGuardado()
    },
  })

  const restablecer = useMutation({
    mutationFn: (nueva: string) => api.post(`/api/v1/usuarios/${usuario.id}/password`, { nueva }),
    onSuccess: () => {
      // Se limpia en el acto: dejarla escrita en pantalla la expone a quien pase
      // por detrás, y ya no hace falta para nada.
      setClave("")
      setHecho(true)
    },
  })

  // Sólo lo que cambió: mandar el nombre intacto en cada guardado dejaría una
  // entrada de bitácora por cada vez que alguien tocó un rol.
  const cambios: Record<string, unknown> = {}
  if (nombre.trim() !== usuario.nombre) cambios.nombre = nombre.trim()
  if ([...elegidos].sort().join() !== [...usuario.roles].sort().join()) {
    cambios.roles = elegidos
  }
  const sucio = Object.keys(cambios).length > 0
  const listo = nombre.trim().length >= 2

  const alternarRol = (nombreRol: string) =>
    setElegidos((previos) =>
      previos.includes(nombreRol)
        ? previos.filter((r) => r !== nombreRol)
        : [...previos, nombreRol],
    )

  return (
    <div>
      {/* Lo que no se toca, arriba y como texto: enseñarlo en un campo apagado
          invita a intentar cambiarlo y a preguntar por qué no deja. */}
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {usuario.email} · {usuario.tipo === "INTERNO" ? "personal" : "importador"}
      </p>

      <label className="mt-3 block text-xs" htmlFor={`${idBase}-nombre`}>
        Nombre
        <input
          id={`${idBase}-nombre`}
          className="campo mt-1"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />
      </label>

      <fieldset className="mt-4">
        <legend className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Roles
        </legend>
        <div className="mt-1 space-y-1">
          {roles.map((r) => (
            <label key={r.id} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={elegidos.includes(r.nombre)}
                onChange={() => alternarRol(r.nombre)}
                data-testid={`rol-usuario-${r.nombre}`}
              />
              <span>
                <span className="flex items-center gap-1">
                  <ShieldCheck size={12} aria-hidden={true} />
                  {r.nombre}
                </span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {r.descripcion}
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {sucio && (
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setNombre(usuario.nombre)
              setElegidos(usuario.roles)
            }}
            className="boton-secundario px-2 py-1 text-xs"
          >
            Descartar
          </button>
          <button
            type="button"
            data-testid="guardar-usuario"
            disabled={!listo || guardar.isPending}
            onClick={() => guardar.mutate(cambios)}
            className="boton-primario px-2 py-1 text-xs disabled:opacity-50"
          >
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      )}

      <hr className="my-4" style={{ borderColor: "var(--border)" }} />

      <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide">
        <KeyRound size={12} aria-hidden={true} />
        Restablecer contraseña
      </h3>
      <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
        No hace falta la actual: quien administra no la sabe. Queda en la bitácora, y{" "}
        <strong>cierra todas las sesiones abiertas de esa persona</strong>.
      </p>
      <div className="mt-2 flex gap-2">
        <input
          type="password"
          aria-label="Contraseña nueva"
          autoComplete="new-password"
          placeholder={`Mínimo ${LARGO_MINIMO_CLAVE} caracteres`}
          className="campo"
          value={clave}
          onChange={(e) => {
            setClave(e.target.value)
            setHecho(false)
          }}
        />
        <button
          type="button"
          data-testid="restablecer-password"
          disabled={clave.length < LARGO_MINIMO_CLAVE || restablecer.isPending}
          onClick={() => restablecer.mutate(clave)}
          className="boton-secundario shrink-0 px-2 py-1 text-xs disabled:opacity-50"
        >
          {restablecer.isPending ? "Guardando…" : "Restablecer"}
        </button>
      </div>
      {hecho && (
        <p role="status" className="mt-1 text-xs" style={{ color: "var(--success)" }}>
          Contraseña cambiada. Sus sesiones abiertas ya no valen.
        </p>
      )}
      {restablecer.isError && (
        <p role="alert" className="mt-1 text-xs" style={{ color: "var(--destructive)" }}>
          {restablecer.error instanceof ErrorApi
            ? restablecer.error.message
            : "No se pudo cambiar la contraseña."}
        </p>
      )}

      <hr className="my-4" style={{ borderColor: "var(--border)" }} />

      <h3 className="text-xs font-medium uppercase tracking-wide">Avisos por Telegram</h3>
      <p
        data-testid="telegram-usuario"
        className="mt-1 flex items-center gap-2 text-sm"
        style={{ color: usuario.telegram_vinculado ? "var(--success)" : "var(--fg-muted)" }}
      >
        {/* Icono Y texto, no sólo color: el estado ES el contenido, y con el color
            solo no llega a quien no lo distingue (WCAG 1.4.1). */}
        {usuario.telegram_vinculado ? (
          <Check size={16} aria-hidden={true} />
        ) : (
          <Minus size={16} aria-hidden={true} />
        )}
        {usuario.telegram_vinculado ? "Enlazado" : "Sin enlazar"}
      </p>
      {!usuario.telegram_vinculado && (
        <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
          Lo enlaza cada quien desde su propia pantalla de Avisos: hace falta un código de un solo
          uso y escribírselo al bot, y por eso no se puede hacer desde aquí.
        </p>
      )}

      <hr className="my-4" style={{ borderColor: "var(--border)" }} />

      <ExpedienteDelUsuario usuarioId={usuario.id} />

      <hr className="my-4" style={{ borderColor: "var(--border)" }} />

      <div className="flex items-center justify-between gap-3">
        <span className="text-sm">
          Estado{" "}
          <Insignia tono={usuario.activo ? "exito" : "neutro"}>
            {usuario.activo ? "activo" : "desactivado"}
          </Insignia>
        </span>
        <button
          type="button"
          disabled={guardar.isPending}
          onClick={() => guardar.mutate({ activo: !usuario.activo })}
          className="boton-secundario px-2 py-1 text-xs disabled:opacity-50"
        >
          {usuario.activo ? "Desactivar" : "Reactivar"}
        </button>
      </div>
      <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
        Desactivar corta la sesión al siguiente request, no al expirar el token. Las cuentas no se
        borran nunca: hacerlo destruiría la evidencia de lo que hicieron.
      </p>

      <hr className="my-4" style={{ borderColor: "var(--border)" }} />

      {/* ⚠ **Va aquí, junto a la persona, y no en la matriz de permisos.** No es un
          permiso: no concede leer reportes —eso sigue siendo `reporte:triar`, que va
          por rol— sino que dice a quién avisa el bot cuando algo se rompe. Ponerlo
          en la matriz lo haría parecer un acceso más, que es justo la confusión que
          esta columna existe para evitar. */}
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm">
          Atiende las fallas {usuario.es_dev && <Insignia tono="primario">dev</Insignia>}
        </span>
        <button
          type="button"
          disabled={guardar.isPending}
          onClick={() => guardar.mutate({ es_dev: !usuario.es_dev })}
          data-testid="alternar-dev"
          className="boton-secundario px-2 py-1 text-xs disabled:opacity-50"
        >
          {usuario.es_dev ? "Quitar de dev" : "Marcar como dev"}
        </button>
      </div>
      <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
        Quien esté marcado recibe por Telegram las fallas que reporte la gente, y se le asignan como
        tarea. <strong>No concede ningún permiso</strong>: leer los reportes sigue dependiendo del
        rol. Es de la persona y no del cargo — de dos administradores, uno puede arreglar el sistema
        y el otro no.
        {usuario.es_dev && !usuario.telegram_vinculado && (
          <>
            {" "}
            <strong>Ojo:</strong> esta persona no tiene Telegram enlazado, así que el aviso sólo le
            llegará a la bandeja del sistema.
          </>
        )}
      </p>

      {guardar.isError && (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
          {guardar.error instanceof ErrorApi
            ? guardar.error.message
            : "No se pudo guardar el cambio."}
        </p>
      )}
    </div>
  )
}

/**
 * A qué persona pertenece esta cuenta.
 *
 * ⚠ **Una cuenta es una credencial y una persona es un expediente**, y desde aquí no
 * había forma de pasar de una a la otra: quien mira «quién es admin@setieca.com.ve»
 * tenía que ir a Personas y buscar a ojo por quién tuviera esa cuenta enlazada.
 *
 * ⚠ **Sin expediente NO es un error.** Una cuenta de servicio no tiene ficha, y la
 * mayoría de la plantilla tiene ficha sin cuenta: son dos cosas que se cruzan, no una
 * jerarquía. Por eso el 404 se lee como «no tiene» y no como un fallo.
 */
function ExpedienteDelUsuario({ usuarioId }: { usuarioId: number }) {
  const ficha = useQuery({
    queryKey: ["persona-de-usuario", usuarioId],
    queryFn: () =>
      api.get<{ id: number; nombres: string; apellidos: string; cargo: string | null }>(
        `/api/v1/personas/de-usuario/${usuarioId}`,
      ),
    // Un 404 es la respuesta normal de «esta cuenta no tiene ficha»: reintentarlo
    // sería insistirle al servidor con algo que ya contestó.
    retry: false,
  })

  return (
    <div>
      <p className="rotulo mb-2">Expediente</p>
      {ficha.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}
      {ficha.isError && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Esta cuenta no tiene ficha de persona. Se enlaza desde Personas, en «Acceso al sistema».
        </p>
      )}
      {ficha.data !== undefined && (
        <p className="text-sm" data-testid="expediente-del-usuario">
          {ficha.data.nombres} {ficha.data.apellidos}
          {ficha.data.cargo !== null && (
            <span style={{ color: "var(--fg-muted)" }}> · {ficha.data.cargo}</span>
          )}{" "}
          <Link to="/personas" className="text-xs underline-offset-2 hover:underline">
            Abrir en Personas
          </Link>
        </p>
      )}
    </div>
  )
}
